import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ShippingEvent {
  id: string;
  name: string;
  project: string;
  location: string;
  timestamp: Date;
  url: string;
}

interface ActiveBuilder {
  id: string;
  name: string;
  currentProject: number;
  startTime: Date;
  location: string;
}

export const MomentumApp: React.FC = () => {
  const [activeBuilders, setActiveBuilders] = useState<ActiveBuilder[]>([]);
  const [recentShipments, setRecentShipments] = useState<ShippingEvent[]>([]);
  const [totalShipped, setTotalShipped] = useState(0);

  // Mock real-time data (replace with WebSocket)
  useEffect(() => {
    const mockActiveBuilders: ActiveBuilder[] = [
      { id: '1', name: 'Sarah Chen', currentProject: 2, startTime: new Date(Date.now() - 45 * 60000), location: 'San Francisco' },
      { id: '2', name: 'Mike Rodriguez', currentProject: 1, startTime: new Date(Date.now() - 20 * 60000), location: 'New York' },
      { id: '3', name: 'Emma Wilson', currentProject: 3, startTime: new Date(Date.now() - 75 * 60000), location: 'London' },
      { id: '4', name: 'Alex Kim', currentProject: 1, startTime: new Date(Date.now() - 10 * 60000), location: 'Seoul' },
    ];

    const mockShipments: ShippingEvent[] = [
      { id: '1', name: 'David Thompson', project: 'Full-Stack App', location: 'Toronto', timestamp: new Date(Date.now() - 2 * 60000), url: 'https://davids-app.pages.dev' },
      { id: '2', name: 'Lisa Park', project: 'Database App', location: 'Singapore', timestamp: new Date(Date.now() - 5 * 60000), url: 'https://lisa-db.workers.dev' },
      { id: '3', name: 'Carlos Silva', project: 'Global API', location: 'São Paulo', timestamp: new Date(Date.now() - 8 * 60000), url: 'https://carlos-api.workers.dev' },
    ];

    setActiveBuilders(mockActiveBuilders);
    setRecentShipments(mockShipments);
    setTotalShipped(1247);

    // Simulate live updates
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        const newShipment: ShippingEvent = {
          id: Date.now().toString(),
          name: ['Alex Chen', 'Maria Garcia', 'James Wilson', 'Sophie Brown'][Math.floor(Math.random() * 4)],
          project: ['Global API', 'Database App', 'Full-Stack App'][Math.floor(Math.random() * 3)],
          location: ['Tokyo', 'Berlin', 'Sydney', 'Mumbai'][Math.floor(Math.random() * 4)],
          timestamp: new Date(),
          url: `https://app-${Date.now()}.pages.dev`
        };
        
        setRecentShipments(prev => [newShipment, ...prev.slice(0, 4)]);
        setTotalShipped(prev => prev + 1);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const getProjectName = (num: number) => {
    const projects = ['🚀 Global API', '🗄️ Database App', '🌐 Full-Stack App'];
    return projects[num - 1] || '🔄 Getting Started';
  };

  const getTimeSpent = (startTime: Date) => {
    const minutes = Math.floor((Date.now() - startTime.getTime()) / 60000);
    return `${minutes}m`;
  };

  const getTimeAgo = (timestamp: Date) => {
    const minutes = Math.floor((Date.now() - timestamp.getTime()) / 60000);
    if (minutes < 1) return 'just now';
    return `${minutes}m ago`;
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="relative overflow-hidden bg-blue-600 p-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto"
        >
          <h1 className="text-4xl font-bold mb-2">🚢 Shipping School Momentum</h1>
          <p className="text-xl opacity-90">Watch developers ship real apps in real-time</p>
        </motion.div>
        
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full opacity-20"
              animate={{
                x: [0, Math.random() * 100],
                y: [0, Math.random() * 100],
              }}
              transition={{
                duration: 10 + Math.random() * 10,
                repeat: Infinity,
                repeatType: "reverse",
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats Panel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="lg:col-span-1 space-y-6"
        >
          {/* Total Shipped Counter */}
          <div className="bg-green-600 rounded-xl p-6 text-center">
            <motion.div
              key={totalShipped}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
              className="text-4xl font-bold mb-2"
            >
              {totalShipped.toLocaleString()}
            </motion.div>
            <div className="text-lg opacity-90">Apps Shipped</div>
            <div className="text-sm opacity-75 mt-1">All time</div>
          </div>

          {/* Active Now */}
          <div className="bg-blue-600 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <h3 className="text-xl font-bold">Building Now</h3>
            </div>
            <div className="text-3xl font-bold">{activeBuilders.length}</div>
            <div className="text-sm opacity-75">Active builders</div>
          </div>

          {/* This Week */}
          <div className="bg-teal-600 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-2">This Week</h3>
            <div className="text-2xl font-bold">156</div>
            <div className="text-sm opacity-75">New graduates</div>
          </div>
        </motion.div>

        {/* Live Activity Feed */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Builders */}
          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
              Currently Building
            </h2>
            <div className="space-y-3">
              <AnimatePresence>
                {activeBuilders.map((builder) => (
                  <motion.div
                    key={builder.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-sm font-bold">
                        {builder.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold">{builder.name}</div>
                        <div className="text-sm opacity-75">{builder.location}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-sm">
                        {getProjectName(builder.currentProject)}
                      </div>
                      <div className="text-xs opacity-75">
                        {getTimeSpent(builder.startTime)} elapsed
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Recent Shipments */}
          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              🎉 Just Shipped
            </h2>
            <div className="space-y-3">
              <AnimatePresence mode="popLayout">
                {recentShipments.map((shipment) => (
                  <motion.div
                    key={shipment.id}
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.9 }}
                    className="flex items-center justify-between p-4 bg-green-600/20 border border-green-500/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        🚀
                      </div>
                      <div>
                        <div className="font-semibold">{shipment.name}</div>
                        <div className="text-sm opacity-75">
                          {shipment.project} • {shipment.location}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <a
                        href={shipment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                      >
                        View Live →
                      </a>
                      <div className="text-xs opacity-75">
                        {getTimeAgo(shipment.timestamp)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-blue-600 rounded-xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold mb-2">Ready to Join Them?</h3>
            <p className="text-lg opacity-90 mb-6">
              Ship your first app in the next 20 minutes
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
            >
              Start Shipping Now
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};